import { useEffect, useRef, useState, useCallback } from 'react'

// Speed levels with clear differences (pixels per second)
const SPEED_LEVELS = [
  { label: '0.5x', value: 30 },
  { label: '1x', value: 60 },
  { label: '1.5x', value: 100 },
  { label: '2x', value: 150 },
  { label: '3x', value: 250 },
  { label: '4x', value: 400 }
]

export default function useAutoScroll({
  idleTimeout = 5000,
  initialSpeedIndex = 1, // Default to 1x (60px/s)
  enabled = true
}) {
  const [isAutoScrolling, setIsAutoScrolling] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [speedIndex, setSpeedIndex] = useState(initialSpeedIndex)

  const animationFrameRef = useRef(null)
  const idleTimerRef = useRef(null)
  const isPausedRef = useRef(false)
  const isScrollingRef = useRef(false)
  const lastTimeRef = useRef(0)
  const speedRef = useRef(SPEED_LEVELS[initialSpeedIndex].value)

  // Keep refs in sync with state
  useEffect(() => {
    isPausedRef.current = isPaused
  }, [isPaused])

  useEffect(() => {
    speedRef.current = SPEED_LEVELS[speedIndex].value
  }, [speedIndex])

  // Speed control functions
  const increaseSpeed = useCallback(() => {
    setSpeedIndex(prev => Math.min(prev + 1, SPEED_LEVELS.length - 1))
  }, [])

  const decreaseSpeed = useCallback(() => {
    setSpeedIndex(prev => Math.max(prev - 1, 0))
  }, [])

  // Get current speed label
  const speedLabel = SPEED_LEVELS[speedIndex].label

  useEffect(() => {
    if (!enabled) return

    // Time-based smooth scroll animation
    const startContinuousScroll = () => {
      if (isPausedRef.current || isScrollingRef.current) return

      setIsAutoScrolling(true)
      isScrollingRef.current = true
      lastTimeRef.current = performance.now()

      const scroll = (currentTime) => {
        if (isPausedRef.current) {
          isScrollingRef.current = false
          setIsAutoScrolling(false)
          return
        }

        // Calculate delta time for consistent speed
        const deltaTime = (currentTime - lastTimeRef.current) / 1000 // Convert to seconds
        lastTimeRef.current = currentTime

        // Clamp deltaTime to prevent huge jumps after tab switch
        const clampedDelta = Math.min(deltaTime, 0.1)

        const maxScroll = document.documentElement.scrollHeight - window.innerHeight
        const currentScroll = window.scrollY

        if (currentScroll >= maxScroll - 5) {
          // Reached the bottom, go back to top smoothly
          isScrollingRef.current = false
          setIsAutoScrolling(false)

          window.scrollTo({ top: 0, behavior: 'smooth' })

          // Wait for scroll to top to complete, then restart
          setTimeout(() => {
            if (!isPausedRef.current) {
              startContinuousScroll()
            }
          }, 2500)
          return
        }

        // Calculate scroll amount based on speed (pixels per second) and delta time
        const scrollAmount = speedRef.current * clampedDelta

        // Use smooth sub-pixel scrolling
        window.scrollBy({
          top: scrollAmount,
          behavior: 'instant'
        })

        animationFrameRef.current = requestAnimationFrame(scroll)
      }

      animationFrameRef.current = requestAnimationFrame(scroll)
    }

    // Stop scrolling
    const stopContinuousScroll = () => {
      setIsAutoScrolling(false)
      isScrollingRef.current = false
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }

    // Reset idle timer
    const resetIdleTimer = () => {
      // Stop current auto-scroll
      stopContinuousScroll()

      // Clear existing idle timer
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
      }

      // Start new idle timer only if not paused
      if (!isPausedRef.current) {
        idleTimerRef.current = setTimeout(() => {
          startContinuousScroll()
        }, idleTimeout)
      }
    }

    // User interaction events
    const interactionEvents = ['mousedown', 'keydown', 'touchstart', 'wheel']

    // Throttle to prevent too many resets
    let lastInteraction = 0
    const handleInteraction = () => {
      const now = Date.now()
      if (now - lastInteraction < 100) return
      lastInteraction = now
      resetIdleTimer()
    }

    // Handle manual scroll (only if not auto-scrolling)
    const handleScroll = () => {
      if (!isScrollingRef.current) {
        handleInteraction()
      }
    }

    // Add event listeners
    interactionEvents.forEach(event => {
      window.addEventListener(event, handleInteraction, { passive: true })
    })
    window.addEventListener('scroll', handleScroll, { passive: true })

    // Start initial idle timer after page loads
    const initialTimer = setTimeout(() => {
      if (!isPausedRef.current) {
        startContinuousScroll()
      }
    }, idleTimeout)

    // Cleanup
    return () => {
      interactionEvents.forEach(event => {
        window.removeEventListener(event, handleInteraction)
      })
      window.removeEventListener('scroll', handleScroll)

      clearTimeout(initialTimer)
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
    }
  }, [enabled, idleTimeout])

  // Toggle pause
  const togglePause = useCallback(() => {
    setIsPaused(prev => {
      const newPaused = !prev
      isPausedRef.current = newPaused

      if (newPaused) {
        // Stop everything when pausing
        setIsAutoScrolling(false)
        isScrollingRef.current = false
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
          animationFrameRef.current = null
        }
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current)
          idleTimerRef.current = null
        }
      }

      return newPaused
    })
  }, [])

  return {
    isAutoScrolling,
    isPaused,
    togglePause,
    speedLabel,
    speedIndex,
    maxSpeedIndex: SPEED_LEVELS.length - 1,
    increaseSpeed,
    decreaseSpeed
  }
}
